import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { useState } from 'react';

import { cn } from '@/lib/shadcn/utils';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/modules/ui/components/alert-dialog/AlertDialog';
import { Button } from '@/modules/ui/components/button/Button';

import {
  AnnotationPicker,
  type AnnotationReason,
  buildAnnotation,
} from './AnnotationPicker';
import { RejectionMessageEditor } from './rejection-message/RejectionMessageEditor';

interface CandidateAnnotationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isPositive: boolean;
  fullName: string;
  /** When rejecting, the candidate whose rejection message is shown for editing. */
  rejection?: { jobId: string; candidateId: string; firstName: string };
  /** `rejectionMessage` is the edited text, or null to send the default one. */
  onConfirm: (
    annotation: string | null,
    rejectionMessage: string | null,
  ) => void;
  isLoading?: boolean;
}

export function CandidateAnnotationDialog({
  open,
  onOpenChange,
  isPositive,
  fullName,
  rejection,
  onConfirm,
  isLoading,
}: CandidateAnnotationDialogProps) {
  const [selectedReasons, setSelectedReasons] = useState<AnnotationReason[]>(
    [],
  );
  const [otherText, setOtherText] = useState('');
  const [rejectionMessage, setRejectionMessage] = useState<string | null>(null);
  const isRejectionMessageEmpty =
    rejectionMessage !== null && rejectionMessage.trim() === '';

  const reset = () => {
    setSelectedReasons([]);
    setOtherText('');
    setRejectionMessage(null);
  };

  const handleConfirm = () => {
    const annotation = buildAnnotation(selectedReasons, otherText);
    const message = rejectionMessage;
    reset();
    onConfirm(annotation, message);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      reset();
    }
    onOpenChange(nextOpen);
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent className="gap-0 sm:max-w-xl">
        <AlertDialogHeader className="gap-1.5">
          <AlertDialogTitle>
            {isPositive
              ? `Why are you submitting ${fullName}?`
              : `Why are you rejecting ${fullName}?`}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {isPositive
              ? 'Your reason teaches the sourcing agent to find more candidates like them.'
              : 'Your reason teaches the sourcing agent to filter out similar profiles.'}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="mt-6">
          <AnnotationPicker
            isPositive={isPositive}
            selectedReasons={selectedReasons}
            onReasonsChange={setSelectedReasons}
            otherText={otherText}
            onOtherTextChange={setOtherText}
          />
        </div>

        {!isPositive && rejection && (
          <div className="mt-6">
            <RejectionMessageEditor
              jobId={rejection.jobId}
              candidateId={rejection.candidateId}
              firstName={rejection.firstName}
              message={rejectionMessage}
              onMessageChange={setRejectionMessage}
            />
          </div>
        )}

        <AlertDialogFooter className="mt-8 sm:justify-end">
          <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
          <Button
            onClick={handleConfirm}
            disabled={isLoading || isRejectionMessageEmpty}
            loading={isLoading}
            className={cn(
              isPositive
                ? 'bg-semantic-560 hover:bg-semantic-560/90'
                : 'bg-semantic-550 hover:bg-semantic-550/90',
            )}
          >
            {isPositive ? (
              <ThumbsUp className="mr-2 size-4" />
            ) : (
              <ThumbsDown className="mr-2 size-4" />
            )}
            {isPositive ? 'Submit candidate' : 'Reject candidate'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
